import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  type SelectProps as MuiSelectProps,
  styled,
} from '@mui/material';
import type { UseFormRegisterReturn } from 'react-hook-form';

const StyledDiv = styled('div', {
  shouldForwardProp: (prop) => prop !== 'inline',
})<{ inline?: boolean }>(({ theme, inline }) => ({
  display: 'flex',
  flexDirection: inline ? 'row' : 'column',
  alignItems: inline ? 'center' : 'default',
  gap: inline ? theme.spacing(2) : theme.spacing(1),
  width: '100%',
}));

const StyledFormControl = styled(FormControl)`
  & label {
    opacity: 0.5;
  }

  & label.Mui-focused {
    opacity: 0.5;
    transform: translate(14px, 9px) scale(1);
  }

  // ensure checkbox for selected items are not displayed
  & .MuiCheckbox-root {
    display: none;
  }
`;

export type SelectProps = MuiSelectProps & {
  inline?: boolean;
  menuItems?: { value: string | number; label: string }[];
  register?: UseFormRegisterReturn<string>;
  helperText?: string;
  placeholder?: string;
  labelDescription?: string;
};

export const Select = (props: SelectProps) => {
  const { inline, label, menuItems, register, helperText, fullWidth, labelDescription, MenuProps: customMenuProps, ...muiProps } = props;

  const showPlaceHolder = !props.value || (Array.isArray(props.value) && props.value.length === 0);

  return (
    <StyledDiv inline={inline}>
      {label && <span className="text-sm leading-normal">{label}</span>}
      {labelDescription && (
        <span className="text-sm leading-normal text-text-neutral-secondary">
          {labelDescription}
        </span>
      )}
      <StyledFormControl size="small" fullWidth={fullWidth ?? true}>
        {showPlaceHolder && <InputLabel>{props.placeholder}</InputLabel>}
        <MuiSelect
          variant="outlined"
          {...muiProps}
          inputProps={{ ...register }}
          MenuProps={{
            ...customMenuProps,
            sx: { maxHeight: 432, ...customMenuProps?.sx },
          }}
        >
          {menuItems?.map((item, index) => {
            return (
              <MenuItem key={item.value} value={item.value} data-testid={`options-${index}`}>
                {item.label}
              </MenuItem>
            );
          })}
          {props.children}
        </MuiSelect>
        <FormHelperText error>{helperText}</FormHelperText>
      </StyledFormControl>
    </StyledDiv>
  );
};
